import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./Hero.scss";
import heroImage from "../../assets/hero.png";
import productWhite from "../../assets/white-box-modal.jpg";
import productBlack from "../../assets/black-box-modal.jpg";
import useModalA11y from "../../hooks/useModalA11y";
import useRecaptcha from "../../hooks/useRecaptcha";
import { API_BASE } from "../../config/api";

export default function Hero() {
	const { getToken } = useRecaptcha();
	const [offset, setOffset] = useState(0);
	const { t } = useTranslation();
	const [showModal, setShowModal] = useState(false);
	const closeModal = useCallback(() => setShowModal(false), []);
	const modalRef = useModalA11y(showModal, closeModal);
	const [color, setColor] = useState("white");
	const [qty, setQty] = useState(1);
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [recipientName, setRecipientName] = useState("");
	const [city, setCity] = useState("");
	const [npBranch, setNpBranch] = useState("");
	const [errors, setErrors] = useState({
		email: false,
		phone: false,
		recipientName: false,
		city: false,
		npBranch: false,
	});

	const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
	const isValidPhone = (value) => value.replace(/\D/g, "").length >= 9;
	const isValidRequired = (value) => value.trim().length > 0;

	// const pricePerItem = 1999;
	const [oldPricePerItem, setOldPricePerItem] = useState(3000);

	// const discountRate = qty >= 3 ? 0.1 : 0;

	// const subtotal = pricePerItem * qty;
	const oldSubtotal = oldPricePerItem * qty;

	// const discountAmount = subtotal * discountRate;
	const total = oldSubtotal;

	useEffect(() => {
		const pingServer = () => {
			fetch(`${API_BASE}/api/health`, {
				method: "GET",
				keepalive: true,
			}).catch(() => {});
		};

		// The payment API is hosted on Render's free tier, which spins the
		// server down after ~15 min idle and takes 30-50s to wake back up.
		// Ping as soon as the page loads (not just when checkout opens) and
		// keep pinging periodically so it never goes cold during a visit.
		pingServer();
		const keepAlive = setInterval(pingServer, 10 * 60 * 1000);

		return () => clearInterval(keepAlive);
	}, []);

	useEffect(() => {
		fetch(`${API_BASE}/api/product/price`)
			.then((res) => (res.ok ? res.json() : Promise.reject()))
			.then((data) => {
				if (typeof data.price === "number" && data.price > 0) {
					setOldPricePerItem(data.price);
				}
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		const resetLoading = () => {
			setLoading(false);
		};

		const handleVisibilityChange = () => {
			if (!document.hidden) {
				resetLoading();
			}
		};

		window.addEventListener("pageshow", resetLoading);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			window.removeEventListener("pageshow", resetLoading);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	const handlePayment = async () => {
		if (loading) return;

		const emailValid = isValidEmail(email);
		const phoneValid = isValidPhone(phone);
		const recipientNameValid = isValidRequired(recipientName);
		const cityValid = isValidRequired(city);
		const npBranchValid = isValidRequired(npBranch);

		if (!emailValid || !phoneValid || !recipientNameValid || !cityValid || !npBranchValid) {
			setErrors({
				email: !emailValid,
				phone: !phoneValid,
				recipientName: !recipientNameValid,
				city: !cityValid,
				npBranch: !npBranchValid,
			});
			return;
		}

		try {
			setLoading(true);

			const recaptchaToken = await getToken("checkout");

			const res = await fetch(`${API_BASE}/api/pay`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					color,
					qty,
					total,
					email: email.trim(),
					phone: phone.trim(),
					address: {
						recipientName: recipientName.trim(),
						city: city.trim(),
						npBranch: npBranch.trim(),
					},
					ref: localStorage.getItem("revibrain_ref") || undefined,
					recaptchaToken,
				}),
			});

			if (!res.ok) {
				throw new Error(`HTTP ${res.status}`);
			}

			const { url, fields } = await res.json();

			if (!url || !fields) {
				throw new Error("Invalid payment response");
			}

			const form = document.createElement("form");
			form.method = "POST";
			form.action = url;

			Object.keys(fields).forEach((key) => {
				const input = document.createElement("input");
				input.type = "hidden";
				input.name = key;
				input.value = Array.isArray(fields[key])
					? fields[key].join(";")
					: fields[key];

				form.appendChild(input);
			});

			document.body.appendChild(form);
			form.submit();
		} catch (err) {
			console.error("Payment error:", err);
			setLoading(false);
		}
	};

	useEffect(() => {
		document.body.style.overflow = showModal ? "hidden" : "auto";

		return () => {
			document.body.style.overflow = "auto";
		};
	}, [showModal]);

	return (
		<section id="main" className="hero">
			<img
				className="hero__bg"
				src={heroImage}
				alt="Revibrain"
				fetchpriority="high"
				loading="eager"
				decoding="async"
				style={{
					transform: `translateY(${offset * 0.5}px) rotate(-1deg)`,
				}}
			/>

			<div className="hero__overlay"></div>

			<div className="hero__content">
				<h1>{t("hero.title")}</h1>
				<p>{t("hero.subtitle")}</p>
				<button className="hero__cta" onClick={() => setShowModal(true)}>
					{t("hero.cta")}
				</button>
			</div>
			{showModal && (
				<div className="paymentModal" onClick={closeModal}>
					<div
						ref={modalRef}
						className="paymentModal__content"
						role="dialog"
						aria-modal="true"
						aria-labelledby="paymentModalTitle"
						tabIndex={-1}
						onClick={(e) => e.stopPropagation()}>
						<button
							className="paymentModal__close"
							aria-label="Close"
							onClick={closeModal}>
							×
						</button>

						<h3 id="paymentModalTitle">Revibrain™</h3>

						<div className="paymentModal__product">
							<img
								src={color === "white" ? productWhite : productBlack}
								alt={`${color === "white" ? "White" : "Black"} Revibrain device`}
							/>
						</div>

						<div className="paymentModal__right">
							<div className="paymentModal__box">
								<div className="paymentModal__price">
									Total: ₴{total.toFixed(0)}
								</div>

								{/* <div className="paymentModal__oldPrice">
									₴{oldSubtotal.toFixed(0)}
								</div> */}

								<div className="paymentModal__qty">
									<button
										aria-label="Decrease quantity"
										onClick={() => setQty(Math.max(1, qty - 1))}>
										−
									</button>
									<span>{qty}</span>
									<button
										aria-label="Increase quantity"
										onClick={() => setQty(qty + 1)}>
										+
									</button>
								</div>
							</div>
							<div className="paymentModal__discount">
								<div className="paymentModal__colors">
									<button
										className={color === "white" ? "active" : ""}
										aria-label="Select white"
										aria-pressed={color === "white"}
										onClick={() => setColor("white")}
									/>
									<button
										className={color === "black" ? "active" : ""}
										aria-label="Select black"
										aria-pressed={color === "black"}
										onClick={() => setColor("black")}
									/>
								</div>
								{/* <div className="paymentModal__text">{t("discount.hint")}</div>
								{qty >= 3 && (
									<div className="paymentModal__badge">
										{t("discount.label")}
									</div>
								)} */}
							</div>

							<div className="paymentModal__contact">
								<label htmlFor="checkoutEmail">{t("hero.emailLabel")}</label>
								<input
									id="checkoutEmail"
									type="email"
									autoComplete="email"
									placeholder={t("hero.emailPlaceholder")}
									value={email}
									aria-invalid={errors.email}
									onChange={(e) => {
										setEmail(e.target.value);
										setErrors((prev) => ({ ...prev, email: false }));
									}}
								/>
								{errors.email && (
									<span className="paymentModal__error">
										{t("hero.emailError")}
									</span>
								)}

								<label htmlFor="checkoutPhone">{t("hero.phoneLabel")}</label>
								<input
									id="checkoutPhone"
									type="tel"
									autoComplete="tel"
									placeholder={t("hero.phonePlaceholder")}
									value={phone}
									aria-invalid={errors.phone}
									onChange={(e) => {
										setPhone(e.target.value);
										setErrors((prev) => ({ ...prev, phone: false }));
									}}
								/>
								{errors.phone && (
									<span className="paymentModal__error">
										{t("hero.phoneError")}
									</span>
								)}

								<label htmlFor="checkoutRecipientName">
									{t("hero.recipientNameLabel")}
								</label>
								<input
									id="checkoutRecipientName"
									type="text"
									autoComplete="name"
									placeholder={t("hero.recipientNamePlaceholder")}
									value={recipientName}
									aria-invalid={errors.recipientName}
									onChange={(e) => {
										setRecipientName(e.target.value);
										setErrors((prev) => ({ ...prev, recipientName: false }));
									}}
								/>
								{errors.recipientName && (
									<span className="paymentModal__error">
										{t("hero.recipientNameError")}
									</span>
								)}

								<label htmlFor="checkoutCity">{t("hero.cityLabel")}</label>
								<input
									id="checkoutCity"
									type="text"
									autoComplete="address-level2"
									placeholder={t("hero.cityPlaceholder")}
									value={city}
									aria-invalid={errors.city}
									onChange={(e) => {
										setCity(e.target.value);
										setErrors((prev) => ({ ...prev, city: false }));
									}}
								/>
								{errors.city && (
									<span className="paymentModal__error">
										{t("hero.cityError")}
									</span>
								)}

								<label htmlFor="checkoutNpBranch">{t("hero.npBranchLabel")}</label>
								<input
									id="checkoutNpBranch"
									type="text"
									inputMode="numeric"
									placeholder={t("hero.npBranchPlaceholder")}
									value={npBranch}
									aria-invalid={errors.npBranch}
									onChange={(e) => {
										setNpBranch(e.target.value);
										setErrors((prev) => ({ ...prev, npBranch: false }));
									}}
								/>
								{errors.npBranch && (
									<span className="paymentModal__error">
										{t("hero.npBranchError")}
									</span>
								)}
							</div>

							<button
								className="paymentModal__pay"
								onClick={handlePayment}
								disabled={loading}>
								{loading ? (
									<>
										<span className="spinner" />
										{t("hero.redirecting")}...
									</>
								) : (
									t("hero.pay")
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</section>
	);
}
